import React from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { SwipeHub } from '../../pages/Swipe/SwipeHub';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface SwipeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 10000;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow: hidden;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  width: auto;
  height: auto;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: all 0.2s ease;
  padding: ${theme.spacing.xs};
  margin: 0;

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2.5;
    color: var(--text-primary);
  }

  &:hover {
    svg {
      color: var(--text-primary);
      transform: scale(1.1);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
    
    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const SwipeContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

export const SwipeModal: React.FC<SwipeModalProps> = ({
  isOpen,
  onClose
}) => {
  // Handle close - just close the modal without navigation
  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  /**
   * Close when the viewer navigates somewhere.
   *
   * This modal is opened by app state, not by the router, and it covers the
   * whole screen. So a link inside it — the deck's "Adjust who you see", which
   * goes to Settings — changed the URL underneath while the modal stayed on
   * top, and the click looked like it did nothing.
   *
   * Handled here rather than at each link, because it is true of every link
   * this modal will ever contain: someone who navigates has asked to be
   * somewhere else, and a full-screen overlay outliving that is a trap.
   */
  const location = useLocation();
  const openedAt = React.useRef(location.pathname);

  React.useEffect(() => {
    if (isOpen) openedAt.current = location.pathname;
    // Only on open: recording the path on every change would defeat the check
    // below, which is the whole point.
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && location.pathname !== openedAt.current) onClose();
  }, [location.pathname, isOpen, onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton
          onClick={handleClose}
          title="Close"
          aria-label="Close swipe modal"
        >
          <X size={24} stroke="currentColor" />
        </CloseButton>
        <SwipeContentWrapper>
          <SwipeHub />
        </SwipeContentWrapper>
      </ModalContent>
    </ModalBackdrop>
  );
};

